import {
  type ConversationPanel,
  type ConversationPanelResponse,
  conversationPanelSchema,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { type PanelLayout, panelLayoutOf, shownPanelFields } from './panel-layouts';
import type { DesktopShellStore } from './store';

/**
 * Each conversation owns its side panel (docs/redesign-implementation-plan.md §6.1): its tabs in
 * order, the one shown, what each app tab shows, open or not, its size. The shown conversation's
 * lives in the store's panel fields, the others in `panelLayouts`; this device remembers them
 * (`panel-layouts.ts`), and a saved conversation keeps it on the server too, so that it comes back
 * on the user's other devices: the newer copy wins.
 */
export type PanelLayoutClient = {
  read(conversation: string): Promise<ConversationPanel | null>;
  write(conversation: string, panel: ConversationPanel): Promise<void>;
};

const path = (conversation: string) =>
  `/api/conversations/${encodeURIComponent(conversation)}/panel`;

const serverPanelLayouts: PanelLayoutClient = {
  async read(conversation) {
    const answer = await apiFetch<ConversationPanelResponse>(path(conversation));
    return answer.panel;
  },
  async write(conversation, panel) {
    await apiFetch(path(conversation), {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(panel),
    });
  },
};

/** A layout as the server keeps it (the transient diff of a message is not kept). */
function panelOfLayout(layout: PanelLayout, updatedAt: number): ConversationPanel | null {
  const parsed = conversationPanelSchema.safeParse({
    open: layout.open,
    tabs: layout.tabs,
    size: layout.size,
    restoreSize: layout.restoreSize,
    appTargets: Object.fromEntries(
      Object.entries(layout.appTargets).map(([id, target]) => [
        id,
        {
          item: target.item,
          ...(target.section === undefined ? {} : { section: target.section }),
          ...(target.route === undefined ? {} : { route: target.route }),
        },
      ]),
    ),
    selectedSubagentId: layout.selectedSubagentId,
    split: layout.split,
    updatedAt,
  });
  return parsed.success ? parsed.data : null;
}

function layoutOfPanel(panel: ConversationPanel): PanelLayout {
  return {
    open: panel.open,
    tabs: panel.tabs as PanelLayout['tabs'],
    size: panel.size,
    restoreSize: panel.restoreSize,
    appTargets: panel.appTargets,
    selectedSubagentId: panel.selectedSubagentId,
    messageReviewDiff: null,
    split: panel.split as PanelLayout['split'],
  };
}

const CHANGED_KEY = 'crowdex.desktop.panel-changed-at';

function readChangedAt(storage: Pick<Storage, 'getItem'> | null): Record<string, number> {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(CHANGED_KEY) ?? '{}');
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

/** The panel fields of the shown conversation changed between two states. */
export function shownPanelChanged(
  state: ReturnType<DesktopShellStore['getState']>,
  previous: ReturnType<DesktopShellStore['getState']>,
): boolean {
  return (
    state.sidePanelOpen !== previous.sidePanelOpen ||
    state.panelTabs !== previous.panelTabs ||
    state.panelSplit !== previous.panelSplit ||
    state.panelSize !== previous.panelSize ||
    state.panelRestoreSize !== previous.panelRestoreSize ||
    state.appPanelTargets !== previous.appPanelTargets ||
    state.selectedSubagentId !== previous.selectedSubagentId
  );
}

/**
 * Keeps the saved conversations' panels on the server: a change of the shown one is written
 * (`delayMs` after the last), a conversation opened (or saved, a draft becoming one) reads its
 * panel and takes it when newer than this device's. Returns the way to stop.
 */
export function syncPanelLayouts(
  store: DesktopShellStore,
  client: PanelLayoutClient = serverPanelLayouts,
  {
    delayMs = 800,
    storage = typeof localStorage === 'undefined' ? null : localStorage,
    now = Date.now,
  }: {
    delayMs?: number;
    storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
    now?: () => number;
  } = {},
): () => void {
  const changedAt = readChangedAt(storage);
  const remember = () => {
    try {
      storage?.setItem(CHANGED_KEY, JSON.stringify(changedAt));
    } catch {
      // Only the comparison with the server's copy is lost.
    }
  };
  const saved = (id: string) => store.getState().syncedThreads.some((thread) => thread.id === id);
  let applying = false;
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const write = (id: string) => {
    const pending = timers.get(id);
    if (pending !== undefined) clearTimeout(pending);
    timers.set(
      id,
      setTimeout(() => {
        timers.delete(id);
        const state = store.getState();
        const layout = id === state.chatId ? panelLayoutOf(state) : state.panelLayouts[id];
        if (layout === undefined || !saved(id)) return;
        const panel = panelOfLayout(layout, changedAt[id] ?? now());
        if (panel !== null) void client.write(id, panel).catch(() => undefined);
      }, delayMs),
    );
  };
  const read = (id: string) => {
    if (!saved(id)) return;
    void client.read(id).then(
      (panel) => {
        if (panel === null || panel.updatedAt <= (changedAt[id] ?? 0)) return;
        changedAt[id] = panel.updatedAt;
        remember();
        const layout = layoutOfPanel(panel);
        applying = true;
        try {
          const state = store.getState();
          if (state.chatId === id) store.setState(shownPanelFields(layout));
          else state.setPanelLayouts({ ...state.panelLayouts, [id]: layout });
        } finally {
          applying = false;
        }
      },
      () => undefined,
    );
  };
  read(store.getState().chatId);
  const unsubscribe = store.subscribe((state, previous) => {
    if (state.chatId !== previous.chatId) {
      read(state.chatId);
      return;
    }
    // A draft saved (an app opened in it, its first message): its panel goes with it.
    const nowSaved = saved(state.chatId);
    if (nowSaved && !previous.syncedThreads.some((thread) => thread.id === state.chatId)) {
      changedAt[state.chatId] ??= now();
      remember();
      write(state.chatId);
    }
    if (applying) return;
    // A parked panel changed (a tab carried away from it, an app prepared in it).
    for (const [id, layout] of Object.entries(state.panelLayouts)) {
      if (layout === previous.panelLayouts[id]) continue;
      changedAt[id] = now();
      remember();
      write(id);
    }
    if (!shownPanelChanged(state, previous)) return;
    changedAt[state.chatId] = now();
    remember();
    write(state.chatId);
  });
  return () => {
    unsubscribe();
    for (const timer of timers.values()) clearTimeout(timer);
  };
}
